import { memo } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { canStepBoardScale } from '../../utils/board-scale';

interface BoardScaleControlProps {
  /** Il passo in vigore (1 = la damiera piu' grande che ci sta). */
  value: number;
  /** `+1` piu' grande, `-1` piu' piccola. */
  onStep: (verso: 1 | -1) => void;
}

/**
 * IL REGOLO: due bottoni sullo spigolo alto-sinistro della damiera, da 900px in su.
 *
 * Il predefinito e' la damiera piu' grande che ci sta, e questo e' giusto — su uno
 * schermo grande deve essere grossa. Mancava il modo di dire «per me e' troppo
 * grande»: `--gs-scala-damiera` scende di un passo per pressione (vedi
 * `utils/board-scale.ts` per i passi e il perche' non si sale oltre il 100%).
 *
 * Sta DENTRO `.gs-board-wrapper` ed e' `position: absolute`: cosi' resta incollato allo
 * spigolo della damiera a ogni misura — la segue quando si rimpicciolisce — e non
 * entra fra i tre figli della banda, che sono e devono restare cartellino, damiera,
 * cartellino. Lo spazio dove vive e' riservato da `.gs-middle:has(.gs-regolo)`.
 *
 * Nessun `.css` proprio di proposito: le sue regole stanno in `GameScreen.css`, che
 * la schermata importa sempre. Un foglio a parte arriverebbe col chunk pigro del
 * componente, ed e' la trappola per cui gli overlay riusati fuori dalla dashboard
 * perdevano lo stile.
 */
export const BoardScaleControl = memo(function BoardScaleControl({ value, onStep }: BoardScaleControlProps) {
  const { t } = useTranslation();

  return (
    <div className="gs-regolo" role="group" aria-label={t('game.actions.boardSize', 'Board size')}>
      <button
        type="button"
        className="gs-regolo__btn"
        data-testid="regolo-piu"
        aria-label={t('game.actions.boardBigger', 'Bigger board')}
        title={t('game.actions.boardBigger', 'Bigger board')}
        /* Al 100% non c'e' niente sopra: la damiera e' GIA' tutto lo spazio che
           c'e'. Disabilitato e non nascosto, o la coppia ballerebbe sotto il dito. */
        disabled={!canStepBoardScale(value, 1)}
        onClick={() => onStep(1)}
      >
        <Plus size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="gs-regolo__btn"
        data-testid="regolo-meno"
        aria-label={t('game.actions.boardSmaller', 'Smaller board')}
        title={t('game.actions.boardSmaller', 'Smaller board')}
        disabled={!canStepBoardScale(value, -1)}
        onClick={() => onStep(-1)}
      >
        <Minus size={14} aria-hidden="true" />
      </button>
    </div>
  );
});

export default BoardScaleControl;
